<script setup lang="ts">
import { FullTableLayout, StackLayout } from '@/components/Layouts'
import { BaseTable, TableColumn, TablePagination } from '@/components/Table'
import TypographyItem from '@/components/Typography/TypographyItem.vue'
import { moment } from '@/locales/langUtils'
import deviceApi from '@/services/device.service'
import { ESortBy, getSortByKey } from '@/types/enum/SortByEnum'
import { type PaginationDataType, defaultPaginationDataType } from '@/types/PaginationType'
import { DATETIME_FORMAT } from '@/utils/dateUtils'
import { getQuerySort } from '@/utils/queryUtils'
import { getPageSize } from '@/utils/routeUtils'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import type { DeviceVersionHistoryModel } from '../../../../../types/models/DeviceVersionHistoryModel'

const { t } = useI18n()

type Props = {
    deviceId?: number
}
const props = defineProps<Props>()

const columns = computed(() => ({
    lastVersion: { field: 'old_value', label: t('device.versionHistory.table.lastVersion') },
    sentVersion: { field: 'new_value', label: t('device.versionHistory.table.sentVersion') },
    sentDate: { field: 'updated_at', label: t('device.versionHistory.table.sentDate') },
    user: { field: 'user', label: t('device.versionHistory.table.user') },
    progress: { field: 'progress', label: t('device.versionHistory.table.progress') }
}))

const sort = ref<ESortBy>(getQuerySort() ?? ESortBy.DATE_RECENT)

const pagination = ref<PaginationDataType<DeviceVersionHistoryModel[]>>(defaultPaginationDataType)
const getHistory = () => {
    if (!props.deviceId) return
    deviceApi
        .getDeviceVersionHistory(props.deviceId, {
            sort: getSortByKey(sort.value),
            per_page: getPageSize() ?? pagination.value?.per_page,
            page: pagination.value?.current_page
        })
        .then(res => (pagination.value = res))
}

const handleChangePageSize = (value: number) => {
    pagination.value = { ...pagination.value, current_page: 1, per_page: value }
    getHistory()
}
const handleChangePageNumber = (value: number) => {
    pagination.value = { ...pagination.value, current_page: value }
    getHistory()
}
const handleChangeSortBy = (sortBy: ESortBy) => {
    sort.value = sortBy
    getHistory()
}

const table = ref()

watch(
    () => props.deviceId,
    newDeviceId => (newDeviceId ? getHistory() : undefined),
    { immediate: true }
)
</script>

<template>
    <StackLayout direction="column" class="full-width" :gap="8">
        <FullTableLayout :sortBy="sort" @changeSortBy="handleChangeSortBy" class="device-history">
            <template #table>
                <BaseTable
                    v-bind:ref="table"
                    :data="pagination.data"
                    :loading="pagination.isLoading">
                    <template #columns>
                        <TableColumn
                            :field="columns.lastVersion.field"
                            :label="columns.lastVersion.label"
                            v-slot="props">
                            <TypographyItem
                                :label="
                                    props.row.old_value
                                        ? props.row.old_value
                                        : $t('common.undefined')
                                " />
                        </TableColumn>
                        <TableColumn
                            :field="columns.sentVersion.field"
                            :label="columns.sentVersion.label" />

                        <TableColumn
                            :field="columns.sentDate.field"
                            :label="columns.sentDate.label"
                            v-slot="props">
                            {{
                                props.row.updated_at
                                    ? moment(props.row.updated_at)
                                          .format(DATETIME_FORMAT)
                                          .toString()
                                    : $t('common.undefined')
                            }}
                        </TableColumn>
                        <TableColumn
                            :field="columns.user.field"
                            :label="columns.user.label"
                            v-slot="props">
                            <TypographyItem :label="props.row.user?.name ?? ''" />
                        </TableColumn>
                        <TableColumn
                            :field="columns.progress.field"
                            :label="columns.progress.label"
                            v-slot="props">
                            <TypographyItem
                                :label="
                                    props.row.progress
                                        ? `${props.row.progress}%`
                                        : $t('common.unknown')
                                " />
                        </TableColumn>
                    </template>
                </BaseTable>
            </template>
            <template #pagination>
                <TablePagination
                    :perPage="pagination.per_page"
                    :currentPage="pagination.current_page"
                    :lastPage="pagination.last_page"
                    :total="pagination.total"
                    @change:pageSize="handleChangePageSize"
                    @change:pageNumber="handleChangePageNumber" />
            </template>
        </FullTableLayout>
    </StackLayout>
</template>

<style scoped lang="scss">
.centered-pagination {
    padding: 8px;
}

.device-history {
    & :deep(.stack) {
        flex-direction: row;
    }
}
</style>
